/**
 * FeaturedReviews - Accessible carousel web component for customer testimonials.
 * Implements WAI-ARIA carousel pattern with keyboard navigation and autoplay.
 * @extends HTMLElement
 */
class FeaturedReviews extends HTMLElement {
  constructor() {
    super();

    this._handleNextClick = this._handleNextClick.bind(this);
    this._handlePrevClick = this._handlePrevClick.bind(this);
    this._handleMouseEnter = this._handleMouseEnter.bind(this);
    this._handleMouseLeave = this._handleMouseLeave.bind(this);
    this._handleFocusIn = this._handleFocusIn.bind(this);
    this._handleFocusOut = this._handleFocusOut.bind(this);
    this._handleKeyDown = this._handleKeyDown.bind(this);
  }

  connectedCallback() {
    this.reviews = this.querySelectorAll(".featured-reviews__slide");
    this.profiles = this.querySelectorAll(".featured-reviews__image");
    this.nextButton = this.querySelector(
      ".prev-next-arrow-button__arrow--next"
    );
    this.previousButton = this.querySelector(
      ".prev-next-arrow-button__arrow--prev"
    );
    this.liveRegion = this.querySelector(".featured-reviews__sr-live-region");

    this.total = this.reviews.length;
    this.current = 0;
    this.autoplayTimer = null;
    this.isRotating = false;
    this.wasManuallyPaused = false;
    this.prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (this.total <= 1) {
      return;
    }

    if (this.nextButton) {
      this.nextButton.classList.add("mut-featured-reviews__arrow--show");
      this.nextButton.classList.add("featured-reviews__arrow--show");
    }
    if (this.previousButton) {
      this.previousButton.classList.add("mut-featured-reviews__arrow--show");
      this.previousButton.classList.add("featured-reviews__arrow--show");
    }

    if (this.nextButton && this.previousButton) {
      this.nextButton.addEventListener("click", this._handleNextClick);
      this.previousButton.addEventListener("click", this._handlePrevClick);
    }

    this.addEventListener("mouseenter", this._handleMouseEnter);
    this.addEventListener("mouseleave", this._handleMouseLeave);
    this.addEventListener("focusin", this._handleFocusIn);
    this.addEventListener("focusout", this._handleFocusOut);
    this.addEventListener("keydown", this._handleKeyDown);

    this.showSlide(0);
    this.initAutoplay();
  }

  /** @returns {void} */
  _handleNextClick() {
    this.stopAutoplay();
    this.wasManuallyPaused = true;
    this.next();
  }

  /** @returns {void} */
  _handlePrevClick() {
    this.stopAutoplay();
    this.wasManuallyPaused = true;
    this.previous();
  }

  /** @returns {void} */
  _handleMouseEnter() {
    if (this.isRotating) {
      this.pauseAutoplay();
    }
  }

  /** @returns {void} */
  _handleMouseLeave() {
    if (!this.wasManuallyPaused) {
      this.resumeAutoplay();
    }
  }

  /** @returns {void} */
  _handleFocusIn() {
    if (this.isRotating) {
      this.pauseAutoplay();
    }
  }

  /**
   * @param {FocusEvent} e
   * @returns {void}
   */
  _handleFocusOut(e) {
    if (!this.contains(e.relatedTarget) && !this.wasManuallyPaused) {
      this.resumeAutoplay();
    }
  }

  /**
   * Handles keyboard navigation per WAI-ARIA carousel pattern.
   * @param {KeyboardEvent} e
   * @returns {void}
   */
  _handleKeyDown(e) {
    switch (e.key) {
      case "ArrowLeft":
        e.preventDefault();
        this.stopAutoplay();
        this.wasManuallyPaused = true;
        this.previous();
        break;
      case "ArrowRight":
        e.preventDefault();
        this.stopAutoplay();
        this.wasManuallyPaused = true;
        this.next();
        break;
      case "Home":
        e.preventDefault();
        this.stopAutoplay();
        this.wasManuallyPaused = true;
        this.goToSlide(0);
        break;
      case "End":
        e.preventDefault();
        this.stopAutoplay();
        this.wasManuallyPaused = true;
        this.goToSlide(this.total - 1);
        break;
    }
  }

  /** @returns {void} */
  initAutoplay() {
    const autoplay = this.dataset.autoplay === "true";
    const delay = parseInt(this.dataset.autoplayDelay, 10) || 10000;
    this.autoplayDelay = Math.max(delay, 5000);

    if (autoplay && !this.prefersReducedMotion) {
      this.startAutoplay();
    }
  }

  /** @returns {void} */
  startAutoplay() {
    if (this.autoplayTimer) return;
    this.isRotating = true;
    this.updateLiveRegion("off");
    this.autoplayTimer = setInterval(() => {
      this.next();
    }, this.autoplayDelay);
  }

  /** @returns {void} */
  stopAutoplay() {
    this.isRotating = false;
    this.updateLiveRegion("polite");
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  /** @returns {void} */
  pauseAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
    this.updateLiveRegion("polite");
  }

  /** @returns {void} */
  resumeAutoplay() {
    if (this.isRotating && !this.autoplayTimer) {
      this.updateLiveRegion("off");
      this.autoplayTimer = setInterval(() => {
        this.next();
      }, this.autoplayDelay);
    }
  }

  /**
   * @param {string} value - "off" or "polite"
   * @returns {void}
   */
  updateLiveRegion(value) {
    if (this.liveRegion) {
      this.liveRegion.setAttribute("aria-live", value);
    }
  }

  /** @returns {void} */
  next() {
    const prev = this.current;
    this.current = (this.current + 1) % this.total;
    this.setSlide(prev, this.current);
  }

  /** @returns {void} */
  previous() {
    const prev = this.current;
    this.current = (this.current - 1 + this.total) % this.total;
    this.setSlide(prev, this.current);
  }

  /**
   * @param {number} index - target slide index
   * @returns {void}
   */
  goToSlide(index) {
    if (index === this.current || index < 0 || index >= this.total) return;
    const prev = this.current;
    this.current = index;
    this.setSlide(prev, this.current);
  }

  /**
   * @param {number} prev - previous slide index
   * @param {number} next - next slide index
   * @returns {void}
   */
  setSlide(prev, next) {
    this.reviews[prev].classList.remove("mut-featured-reviews--show", "featured-reviews--show");
    this.reviews[prev].setAttribute("aria-hidden", "true");
    this.profiles[prev].classList.remove("mut-featured-reviews--show", "featured-reviews--show");
    this.profiles[prev].setAttribute("aria-hidden", "true");
    this.showSlide(next);
  }

  /**
   * @param {number} index - slide index to show
   * @returns {void}
   */
  showSlide(index) {
    this.reviews[index].classList.add("mut-featured-reviews--show", "featured-reviews--show");
    this.reviews[index].setAttribute("aria-hidden", "false");
    this.profiles[index].classList.add("mut-featured-reviews--show", "featured-reviews--show");
    this.profiles[index].setAttribute("aria-hidden", "false");

    if (this.liveRegion) {
      this.liveRegion.textContent = `Review ${index + 1} of ${this.total}`;
    }
  }

  disconnectedCallback() {
    this.stopAutoplay();

    if (this.nextButton) {
      this.nextButton.removeEventListener("click", this._handleNextClick);
    }
    if (this.previousButton) {
      this.previousButton.removeEventListener("click", this._handlePrevClick);
    }

    this.removeEventListener("mouseenter", this._handleMouseEnter);
    this.removeEventListener("mouseleave", this._handleMouseLeave);
    this.removeEventListener("focusin", this._handleFocusIn);
    this.removeEventListener("focusout", this._handleFocusOut);
    this.removeEventListener("keydown", this._handleKeyDown);
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    customElements.define("featured-reviews", FeaturedReviews);
  });
} else {
  customElements.define("featured-reviews", FeaturedReviews);
}
